import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appAutoScroll]',
  standalone: true,
})
/**
 * @directive AutoScrollDirective
 *
 *  This directive automatically scrolls the element to the bottom whenever new content is added.
 *  It also monitors user scrolling: if the user scrolls away from the bottom (beyond a predefined threshold),
 *  auto-scrolling is temporarily disabled.
 *
 *  To externally re-enable auto-scrolling (for instance, when a new request is made), provide an RxJS Subject
 *  via the {@link autoScrollReset} input and emit a value on that subject. The directive subscribes to this subject
 *  and resets the auto-scroll state to `true` each time an event is received.
 *
 * @example
 * // html:
 * <div class="messages-container" appAutoScroll [autoScrollReset]="autoScrollReset$">
 *   <!-- your sexy code -->
 * </div>
 *
 * // ts:
 * export class SomeComponent {
 *   autoScrollReset$ = new Subject<void>();
 *
 *   sendRequest(userInput: string): void {
 *     ... // your sexy code
 *     // make sure to re-enable auto-scroll for new content (directive disables auto-scroll whenever users manually scrolls)
 *     this.autoScrollReset$.next();
 *   }
 * }
 */
export class AutoScrollDirective implements AfterViewInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  /**
   * Input property to externally control whether auto-scroll is enabled.
   * Set this to `true` (e.g., when a new request is sent) to re-enable auto-scrolling.
   */
  private _autoScrollReset: Subject<void>;
  @Input()
  set autoScrollReset(value: Subject<void>) {
    if (this._autoScrollResetSubscription) {
      this._autoScrollResetSubscription.unsubscribe();
    }
    this._autoScrollReset = value;
    if (this._autoScrollReset) {
      this._autoScrollResetSubscription = this._autoScrollReset.pipe(takeUntil(this._destroyStream))
        .subscribe(() => (this.isAutoScrollEnabled = true));
    }
  }

  /**
   * Internal flag to determine if auto-scrolling is allowed.
   * This flag may be disabled when the user manually scrolls away from the bottom.
   */
  private isAutoScrollEnabled: boolean = true;
  /** MutationObserver instance used to detect DOM changes in the element.*/
  private observer: MutationObserver;
  /** Scroll event listener callback.*/
  private scrollListener: (event: Event) => void;
  /** Pixel threshold to determine if the user has scrolled away from the bottom.*/
  private scrollCheckerThresholdPx: number = 30;
  private _autoScrollResetSubscription: Subscription;

  constructor(private el: ElementRef, private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => {
        if (this.isAutoScrollEnabled) {
          const nativeEl = this.el.nativeElement as HTMLElement;
          nativeEl.scrollTo({ top: nativeEl.scrollHeight, behavior: 'smooth' });
        }
      });
      this.observer.observe(this.el.nativeElement as HTMLElement, { childList: true, subtree: true });
      this.scrollListener = this.onScrollCheckScrollPosition.bind(this) as (event: Event) => void;
      const nativeEl = this.el.nativeElement as HTMLElement;
      nativeEl.addEventListener('scroll', this.scrollListener);
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    const element = this.el.nativeElement as HTMLElement;
    element.removeEventListener('scroll', this.scrollListener);
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  /** Checks if the user has scrolled away from the bottom (beyond the defined threshold) and disables auto-scrolling if so. */
  private onScrollCheckScrollPosition(): void {
    const nativeEl = this.el.nativeElement as HTMLElement;
    const hasUserScrolled = nativeEl.scrollHeight - nativeEl.scrollTop - nativeEl.clientHeight > this.scrollCheckerThresholdPx;
    this.isAutoScrollEnabled = !hasUserScrolled;
  }
}
